import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  IoSearchOutline,
  IoRefreshOutline,
  IoFilterOutline,
  IoTimeOutline,
  IoPersonOutline,
  IoCodeSlashOutline,
  IoCheckmarkCircleOutline,
  IoAlertCircleOutline,
  IoChevronBackOutline,
  IoChevronForwardOutline,
} from 'react-icons/io5';

import { fetchActivityLogs } from '../../services/api';

// ─── Helpers ────────────────────────────────────────────────────────────────

const METHOD_COLORS = {
  POST: 'bg-green-500/15 text-green-400 border border-green-500/30',
  PUT: 'bg-blue-500/15 text-blue-400 border border-blue-500/30',
  PATCH: 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30',
  DELETE: 'bg-red-500/15 text-red-400 border border-red-500/30',
};

const STATUS_COLORS = {
  success: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30',
  error: 'bg-red-500/15 text-red-400 border border-red-500/30',
};

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(new Date(iso));
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const useDebounce = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

// ─── Sub-components ─────────────────────────────────────────────────────────

const MethodBadge = ({ method }) => (
  <span
    className={`inline-flex items-center rounded px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide ${METHOD_COLORS[method] || 'border border-gray-500/30 bg-gray-500/15 text-gray-400'}`}
  >
    {method}
  </span>
);

const StatusBadge = ({ status }) => {
  const isOk = status === 'success';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold ${isOk ? STATUS_COLORS.success : STATUS_COLORS.error}`}
    >
      {isOk ? (
        <IoCheckmarkCircleOutline className="text-xs" />
      ) : (
        <IoAlertCircleOutline className="text-xs" />
      )}
      {isOk ? 'success' : 'failed'}
    </span>
  );
};

const LogRow = ({ log }) => (
  <div className="bg-1 br-1 group flex flex-col gap-2 rounded-lg border p-4 shadow-sm transition-all duration-200 hover:border-[#5652CF]/40 hover:shadow-md md:flex-row md:items-start md:gap-4">
    {/* Timestamp column */}
    <div className="flex min-w-[160px] flex-col gap-0.5">
      <span className="text-p-text text-xs font-medium">
        {formatTimestamp(log.timestamp)}
      </span>
      <span className="text-s-text text-xs">{timeAgo(log.timestamp)}</span>
    </div>

    {/* Actor + action column */}
    <div className="flex flex-1 flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#a5a3ff]">
          <IoPersonOutline className="shrink-0" />
          {log.username || 'Unknown'}
          {log.email && (
            <span className="text-s-text font-normal">({log.email})</span>
          )}
        </span>
      </div>
      <span className="text-p-text text-sm">{log.action}</span>
      <div className="flex flex-wrap items-center gap-2">
        <MethodBadge method={log.method} />
        <span className="text-s-text max-w-xs truncate font-mono text-[10px]">
          {log.path}
        </span>
        <StatusBadge status={log.status} />
      </div>
    </div>
  </div>
);

const SkeletonRow = () => (
  <div className="bg-1 br-1 flex animate-pulse gap-4 rounded-lg border p-4">
    <div className="bg-2 h-10 w-36 rounded" />
    <div className="flex-1 space-y-2">
      <div className="bg-2 h-4 w-3/4 rounded" />
      <div className="bg-2 h-3 w-1/2 rounded" />
    </div>
  </div>
);

// ─── Filters Bar ─────────────────────────────────────────────────────────────

const METHODS = ['ALL', 'POST', 'PUT', 'PATCH', 'DELETE'];

const FiltersBar = ({
  searchAction,
  setSearchAction,
  methodFilter,
  setMethodFilter,
  onRefresh,
  isLoading,
}) => (
  <div className="bg-1 br-1 flex flex-col gap-3 rounded-xl border p-4 shadow-md md:flex-row md:items-center">
    {/* Action search */}
    <div className="relative flex-1">
      <IoSearchOutline className="text-s-text pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" />
      <input
        id="activity-log-search"
        type="text"
        placeholder="Search actions… (e.g. 'created a task')"
        value={searchAction}
        onChange={(e) => setSearchAction(e.target.value)}
        className="bg-2 br-1 text-p-text w-full rounded-lg border py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
      />
    </div>

    {/* Method filter pills */}
    <div className="flex items-center gap-1">
      <IoFilterOutline className="text-s-text mr-1 shrink-0" />
      {METHODS.map((m) => (
        <button
          key={m}
          onClick={() => setMethodFilter(m === 'ALL' ? '' : m)}
          className={`rounded px-2.5 py-1 text-xs font-semibold transition-colors ${
            (methodFilter === '' && m === 'ALL') || methodFilter === m
              ? 'bg-[#5652CF] text-white'
              : 'bg-2 text-s-text hover:bg-3'
          }`}
        >
          {m}
        </button>
      ))}
    </div>

    {/* Refresh */}
    <button
      onClick={onRefresh}
      disabled={isLoading}
      title="Refresh logs"
      className="flex items-center gap-2 rounded-lg bg-[#5652CF]/10 px-3 py-2 text-sm font-medium text-[#a5a3ff] transition-colors hover:bg-[#5652CF]/20 disabled:cursor-wait disabled:opacity-50"
    >
      <IoRefreshOutline
        className={`text-base ${isLoading ? 'animate-spin' : ''}`}
      />
      Refresh
    </button>
  </div>
);

// ─── Stats Strip ─────────────────────────────────────────────────────────────

const StatsStrip = ({ totalLogs }) => (
  <div className="flex flex-wrap items-center gap-4">
    <div className="bg-1 br-1 flex items-center gap-2 rounded-lg border px-4 py-2">
      <IoTimeOutline className="text-[#a5a3ff]" />
      <span className="text-s-text text-sm">
        Total log entries:{' '}
        <strong className="text-p-text">{totalLogs.toLocaleString()}</strong>
      </span>
    </div>
    <p className="text-s-text text-xs">
      Logs are automatically pruned after{' '}
      <strong className="text-p-text">90 days</strong>.
    </p>
  </div>
);

// ─── Pagination ───────────────────────────────────────────────────────────────

const LogPagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const pages = [];
  const delta = 2;
  for (
    let i = Math.max(1, currentPage - delta);
    i <= Math.min(totalPages, currentPage + delta);
    i++
  ) {
    pages.push(i);
  }

  return (
    <div className="flex items-center justify-center gap-2">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="text-s-text hover:bg-2 flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40"
      >
        <IoChevronBackOutline />
        Prev
      </button>

      {pages[0] > 1 && (
        <>
          <button
            onClick={() => onPageChange(1)}
            className="text-s-text hover:bg-2 rounded-lg px-3 py-1.5 text-sm transition-colors"
          >
            1
          </button>
          {pages[0] > 2 && <span className="text-s-text">…</span>}
        </>
      )}

      {pages.map((p) => (
        <button
          key={p}
          onClick={() => onPageChange(p)}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
            p === currentPage
              ? 'bg-[#5652CF] text-white'
              : 'text-s-text hover:bg-2'
          }`}
        >
          {p}
        </button>
      ))}

      {pages[pages.length - 1] < totalPages && (
        <>
          {pages[pages.length - 1] < totalPages - 1 && (
            <span className="text-s-text">…</span>
          )}
          <button
            onClick={() => onPageChange(totalPages)}
            className="text-s-text hover:bg-2 rounded-lg px-3 py-1.5 text-sm transition-colors"
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="text-s-text hover:bg-2 flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40"
      >
        Next
        <IoChevronForwardOutline />
      </button>
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

const PAGE_LIMIT = 50;

const ActivityLogsPanel = ({ userIdToken }) => {
  const [logsData, setLogsData] = useState({
    logs: [],
    total_logs: 0,
    total_pages: 1,
    current_page: 1,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchAction, setSearchAction] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const debouncedAction = useDebounce(searchAction, 400);
  const listRef = useRef(null);

  const loadLogs = useCallback(
    async (page, action, method) => {
      if (!userIdToken) return;
      setIsLoading(true);
      try {
        const params = { page, limit: PAGE_LIMIT };
        if (action) params.action = action;
        if (method) params.method = method;
        const data = await fetchActivityLogs(userIdToken, params);
        setLogsData(data);
      } catch (err) {
        toast.error(
          `Failed to load activity logs: ${err?.message || 'Unknown error'}`,
        );
        setLogsData({
          logs: [],
          total_logs: 0,
          total_pages: 1,
          current_page: 1,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [userIdToken],
  );

  // Load when filters or page changes
  useEffect(() => {
    setCurrentPage(1);
    loadLogs(1, debouncedAction, methodFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAction, methodFilter, refreshKey]);

  useEffect(() => {
    loadLogs(currentPage, debouncedAction, methodFilter);
    // Scroll back to top of the list on page change
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleRefresh = () => setRefreshKey((k) => k + 1);
  const handlePageChange = (page) => {
    if (page >= 1 && page <= logsData.total_pages) setCurrentPage(page);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-p-text flex items-center gap-2 text-xl font-bold">
            <IoCodeSlashOutline className="text-[#a5a3ff]" />
            Activity Logs
          </h2>
          <p className="text-s-text mt-0.5 text-sm">
            Human-readable audit trail of all API actions across the platform.
          </p>
        </div>
      </div>

      {/* Stats */}
      <StatsStrip totalLogs={logsData.total_logs} />

      {/* Filters */}
      <FiltersBar
        searchAction={searchAction}
        setSearchAction={setSearchAction}
        methodFilter={methodFilter}
        setMethodFilter={setMethodFilter}
        onRefresh={handleRefresh}
        isLoading={isLoading}
      />

      {/* Log feed */}
      <div ref={listRef} className="space-y-2">
        {isLoading ? (
          [...Array(8)].map((_, i) => <SkeletonRow key={i} />)
        ) : logsData.logs.length === 0 ? (
          <div className="bg-1 br-1 flex flex-col items-center justify-center gap-3 rounded-xl border py-16 text-center">
            <IoTimeOutline className="text-s-text text-4xl" />
            <p className="text-p-text font-medium">No activity logs found</p>
            <p className="text-s-text text-sm">
              Logs will appear here once users start making API calls.
            </p>
          </div>
        ) : (
          logsData.logs.map((log, idx) => (
            // Use idx as fallback key; logs have no unique _id exposed (excluded by backend)
            <LogRow key={`${log.timestamp}-${idx}`} log={log} />
          ))
        )}
      </div>

      {/* Pagination */}
      {!isLoading && logsData.total_pages > 1 && (
        <LogPagination
          currentPage={currentPage}
          totalPages={logsData.total_pages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Page indicator */}
      {!isLoading && logsData.total_logs > 0 && (
        <p className="text-s-text text-center text-xs">
          Showing page <strong className="text-p-text">{currentPage}</strong> of{' '}
          <strong className="text-p-text">{logsData.total_pages}</strong> —{' '}
          {logsData.total_logs.toLocaleString()} total entries
        </p>
      )}
    </div>
  );
};

export default ActivityLogsPanel;
